/**
 * API Types for Web UI
 *
 * These types are copied from @context-bank/core/api/web-ui/types
 * to avoid build dependency issues.
 *
 * TODO: Once core package is properly published, import from there instead.
 */

import { z } from "zod";

export const WORKSPACE_ID_REGEX = /^[A-Za-z0-9][A-Za-z0-9_-]{1,127}$/;

// ─── BOX API Types ───

/** File upload metadata schema */
export const FileUploadMetadataSchema = z.object({
  workspaceId: z.string().regex(WORKSPACE_ID_REGEX),
  filename: z.string(),
  size: z.number().positive(),
  mimeType: z.string(),
  tags: z.array(z.string()).optional(),
  category: z.string().optional(),
  visibility: z.enum(["private", "team", "organization"]).optional().default("private"),
});

export type FileUploadMetadata = z.infer<typeof FileUploadMetadataSchema>;

export interface BoxUploadInput {
  file: File;
  metadata: Omit<FileUploadMetadata, 'data'>;
}

/** Upload status */
export type UploadStatus = "pending" | "processing" | "completed" | "failed" | "cancelled";

/** BOX upload response */
export interface BoxUploadResponse {
  success: boolean;
  uploadId: string;
  status: UploadStatus;
  message?: string;
  documentId?: string;
}

/** BOX status response */
export interface BoxStatusResponse {
  uploadId: string;
  status: UploadStatus;
  progress?: number;
  documentId?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

/** BOX history item */
export interface BoxHistoryItem {
  uploadId: string;
  filename: string;
  size: number;
  status: UploadStatus;
  documentId?: string;
  tags?: string[];
  createdAt: string;
  completedAt?: string;
}

/** BOX history response */
export interface BoxHistoryResponse {
  items: BoxHistoryItem[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Document Detail API Types ───

export interface DocumentCognifySummary {
  entities: Array<{ type: string; value: string }>;
  actions: Array<{ subject: string; predicate: string; object: string }>;
  decisions: Array<{ subject: string; predicate: string; object: string }>;
}

export interface DocumentDetailResponse {
  id: string;
  sourceUri: string;
  rawTextPreview: string;
  rawTextLength: number;
  createdAt: string;
  status: "processing" | "completed";
  chunkCount: number;
  cognify: DocumentCognifySummary | null;
}

// ─── Adapters API Types ───

/** Adapter type */
export type AdapterType =
  | "line"
  | "fieldy"
  | "slack"
  | "gmail"
  | "kintone"
  | "chatwork"
  | "notion"
  | "plaud"
  | "omi"
  | "google";

/** Adapter status */
export type AdapterStatus = "connected" | "disconnected" | "error" | "preparing";

/** Adapter info */
export interface AdapterUpdateInfo {
  provider: string;
  owner: string;
  id: string;
  targetId: string;
  state: "unknown" | "running" | "success" | "no_change" | "partial" | "failed" | "disabled" | "push";
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastFinishedAt: string | null;
  nextDueAt: string | null;
  lastOutcome: {
    status: "success" | "partial" | "failed";
    checked?: number;
    changed?: number;
    failed?: number;
    observedThrough?: string;
    errorCode?: string;
    retryable?: boolean;
  } | null;
  consecutiveFailures: number;
}

export interface AdapterInfo {
  type: AdapterType;
  name: string;
  status: AdapterStatus;
  webhookUrl?: string;
  lastSync?: string;
  updates?: AdapterUpdateInfo[] | null;
  error?: string;
  config?: Record<string, unknown>;
}

/** Adapters list response */
export interface AdaptersListResponse {
  adapters: AdapterInfo[];
  updates?: AdapterUpdateInfo[] | null;
}

/** Adapter connect request schema */
export const AdapterConnectRequestSchema = z.object({
  workspaceId: z.string().regex(WORKSPACE_ID_REGEX),
  dryRun: z.boolean().optional().default(false),
  config: z.record(z.unknown()).optional(),
  credentialsRef: z.string().optional(),
  healthCheckUrl: z.string().optional(),
});

export type AdapterConnectRequest = z.infer<typeof AdapterConnectRequestSchema>;

/** Adapter disconnect request schema */
export const AdapterDisconnectRequestSchema = z.object({
  workspaceId: z.string().regex(WORKSPACE_ID_REGEX),
  dryRun: z.boolean().optional().default(false),
});

export type AdapterDisconnectRequest = z.infer<typeof AdapterDisconnectRequestSchema>;

/** Adapter connect response */
export interface AdapterConnectResponse {
  success: boolean;
  adapter: AdapterInfo;
  dryRun?: boolean;
  validationErrors?: string[];
  authUrl?: string;
}

export interface AdapterDisconnectResponse {
  success: boolean;
  adapter?: AdapterInfo;
  dryRun?: boolean;
  message?: string;
}

// ─── ACL API Types ───

/** Default ACL settings schema */
export const DefaultACLSettingsSchema = z.object({
  defaultVisibility: z.enum(["private", "team", "organization"]).default("private"),
  requireApprovalForSharing: z.boolean().default(true),
  shareSummaryOnly: z.boolean().default(true),
});

export type DefaultACLSettings = z.infer<typeof DefaultACLSettingsSchema>;

/** ACL rule */
export interface ACLRule {
  role: string;
  permission: "full" | "summary" | "none";
  requireApproval: boolean;
}

/** ACL category */
export interface ACLCategory {
  id: string;
  name: string;
  icon?: string;
  rules: ACLRule[];
  createdAt: string;
  updatedAt: string;
}

/** Create ACL category request schema */
export const CreateACLCategorySchema = z.object({
  name: z.string().min(1).max(100),
  icon: z.string().optional(),
  rules: z.array(
    z.object({
      role: z.string(),
      permission: z.enum(["full", "summary", "none"]),
      requireApproval: z.boolean(),
    })
  ),
});

export type CreateACLCategoryRequest = z.infer<typeof CreateACLCategorySchema>;

/** Share request */
export interface ShareRequest {
  id: string;
  documentId: string;
  requester: {
    id: string;
    name: string;
    role: string;
  };
  requestedPermission: "full" | "summary";
  reason?: string;
  preview: string;
  createdAt: string;
  status: "pending" | "approved" | "rejected";
}

/** Share requests response */
export interface ShareRequestsResponse {
  requests: ShareRequest[];
  total: number;
}

/** Approve/reject request schema */
export const ApproveRejectRequestSchema = z.object({
  reason: z.string().optional(),
});

export type ApproveRejectRequest = z.infer<typeof ApproveRejectRequestSchema>;

// ─── Proposals API Types ───

export type ProposalStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "executed"
  | "expired"
  | "failed";

export type ProposalActionScope =
  | "internal_readonly"
  | "internal_write"
  | "external_contact"
  | "external_write";

export type ProposalRiskLevel = "low" | "medium" | "high";

export interface ProposalTrigger {
  type: "cron" | "event" | "anomaly";
  ref?: string;
}

export interface ProposalEvidenceSource {
  documentId: string;
  chunkId?: string;
  excerpt?: string;
  score?: number;
  sourceType?: string;
  createdAt?: string;
}

export interface ProposalTrace {
  version: "0.1.0";
  conversationId: string;
  modelId: string;
  promptTokens: number;
  completionTokens: number;
  recallQuerySummary: string;
  recallResultIds: string[];
  decisionFactors: string[];
  confidenceScore: number;
  generatedAt: string;
}

export interface ProposalEvidencePack {
  sources: ProposalEvidenceSource[];
  reasoning: string;
  alternatives?: string[];
  trace?: ProposalTrace;
}

export interface ProposalListItem {
  id: string;
  title?: string;
  summary?: string;
  status: ProposalStatus;
  actionScope: ProposalActionScope;
  requiresApproval: boolean;
  riskLevel: ProposalRiskLevel;
  priority: number;
  templateId: string;
  createdAt: string;
  expiresAt?: string;
}

export interface ProposalListResponse {
  items: ProposalListItem[];
  total: number;
}

export interface ProposalEventItem {
  id: string;
  action: string;
  actorId?: string;
  actorRole?: string;
  occurredAt: string;
  metadata?: Record<string, unknown>;
}

export interface ProposalDetail {
  id: string;
  workspaceId: string;
  templateId: string;
  status: ProposalStatus;
  actionScope: ProposalActionScope;
  requiresApproval: boolean;
  riskLevel: ProposalRiskLevel;
  title?: string;
  summary?: string;
  body: string;
  payload?: Record<string, unknown>;
  evidence: ProposalEvidencePack;
  priority: number;
  score?: number;
  trigger: ProposalTrigger;
  dedupKey?: string;
  parentProposalId?: string;
  resubmitCount: number;
  maxResubmits: number;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  executedAt?: string;
  executionResult?: Record<string, unknown>;
  retryCount: number;
  maxRetries: number;
}

export interface ProposalDetailResponse {
  proposal: ProposalDetail;
  events: ProposalEventItem[];
  trace?: ProposalTrace;
}

export interface ProposalActionResponse {
  success: boolean;
  proposal: ProposalDetail;
  event?: ProposalEventItem;
}

// ─── Memory API 型 ───

export interface MemoryCategory {
  id: string;
  name: string;
  description?: string;
  summary?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MemoryItem {
  id: string;
  categoryId: string;
  title: string;
  description?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface MemoryResource {
  id: string;
  itemId: string;
  sourceType: string;
  sourceId?: string;
  label?: string;
  excerpt?: string;
  url?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface MemoryLink {
  id: string;
  fromItemId: string;
  toItemId: string;
  relation: string;
  createdAt: string;
}

export interface MemoryCategoriesResponse {
  categories: MemoryCategory[];
}

export interface MemoryItemsResponse {
  items: MemoryItem[];
}

export interface MemoryResourcesResponse {
  resources: MemoryResource[];
}

export interface MemoryLinksResponse {
  links: MemoryLink[];
}

export interface MemoryAssetExportResponse {
  categories: Array<{
    category: MemoryCategory;
    items: Array<{
      item: MemoryItem;
      resources: MemoryResource[];
    }>;
  }>;
}

// ─── Workspace Features ───

export interface WorkspaceFeaturesData {
  ingest: boolean;
  history_search: boolean;
  dialogue: boolean;
  proposal_generation: boolean;
  line_notification: boolean;
}

export interface WorkspaceFeaturesResponse {
  data: WorkspaceFeaturesData;
}

export interface UpdateWorkspaceFeaturesRequest {
  features: Partial<WorkspaceFeaturesData>;
}

// ─── Onboarding API Types ───

export type OnboardingPreset = "general" | "manufacturing" | "recruitment";

export interface CreateOnboardingWorkspaceRequest {
  slug: string;
  name: string;
}

export interface OnboardingWorkspaceResponse {
  workspaceId: string;
  slug: string;
  name: string;
}

export interface OnboardingAutoSetupResponse {
  started: boolean;
  pipelineOk: boolean;
}

export interface OnboardingConnectedResponse {
  ok: boolean;
}

export interface VisibleWorkspaceInfo {
  id: string;
  slug: string;
  name: string;
  role: "admin" | "member";
  displayName: string | null;
  isPrimary: boolean;
}

export interface VisibleWorkspaceListResponse {
  workspaces: VisibleWorkspaceInfo[];
  total: number;
  primaryWorkspaceId: string | null;
}

export interface VisibleWorkspaceResponse {
  workspace: VisibleWorkspaceInfo | null;
}

export interface WorkspaceStatusResponse {
  /** 接続済みアダプター数 */
  connectedAdapters: number;
  /** 蓄積ドキュメント数 */
  documentCount: number;
  /** オンボーディング開始日時（ISO8601）。未開始の場合 null */
  onboardingStartedAt: string | null;
  /** heartbeat が有効かどうか */
  heartbeatEnabled: boolean;
  /** オンボーディング開始からの経過時間（時）。未開始の場合 null */
  hoursElapsed: number | null;
}

// ─── KPI API Types ───

export interface KpiSummary {
  /** 提案採択率（承認 / (承認+却下)） */
  adoptionRate: number;
  /** 提案無視率（期限切れ / 総提案数） */
  ignoreRate: number;
  /** 再提案成功率（修正後に採択される割合） */
  resubmitRate: number;
  /** 週次トレンド */
  trends: Array<{
    date: string;
    adoptionRate: number;
    ignoreRate: number;
    resubmitRate: number;
    totalProposals: number;
  }>;
}

export interface KpiStats {
  /** 日別提案数 */
  byDate: Array<{
    date: string;
    count: number;
    approved: number;
    rejected: number;
    expired: number;
  }>;
  /** ステータス別集計 */
  byStatus: Array<{
    status: ProposalStatus;
    count: number;
    percentage: number;
  }>;
  /** テンプレート別採択率 */
  byTemplate: Array<{
    templateId: string;
    templateName: string;
    total: number;
    approved: number;
    adoptionRate: number;
  }>;
}

export interface KpiSummaryResponse {
  summary: KpiSummary;
}

export interface KpiStatsResponse {
  stats: KpiStats;
  period: "week" | "month";
}

// ─── Monitoring API Types (S-3) ───

export interface HeartbeatHealth {
  running: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
}

export interface MonitoringHealthResponse {
  heartbeat: HeartbeatHealth;
  errorRate: number;
  status: "healthy" | "degraded" | "down";
}

export interface MetricsSummary {
  periodStart: string;
  periodEnd: string;
  workspaceId: string;
  proposalAcceptanceRate: number;
  proposalApprovedCount: number;
  proposalRejectedCount: number;
  proposalExpiredCount: number;
  avgSummaryQualityScore: number;
  cognifyDataUsageRate: number;
  dialogueResolutionRate: number;
  avgDialogueTurns: number;
  avgEntityCount: number;
  avgExtractionDurationMs: number;
  errorRate: number;
  errorsByComponent: Partial<Record<string, number>>;
}

export interface MonitoringMetricsResponse {
  summary: MetricsSummary;
}

// ─── Workspace Admin API Types (Q-1) ───

export type WorkspaceRole = "owner" | "admin" | "member" | "viewer";
export type WorkspaceStatus = "active" | "suspended" | "trial";

export interface WorkspaceInfo {
  id: string;
  name: string;
  status: WorkspaceStatus;
  plan: string;
  memberCount: number;
  storageUsedBytes: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceListResponse {
  workspaces: WorkspaceInfo[];
  total: number;
}

export interface WorkspaceMember {
  id: string;
  workspaceId: string;
  userId: string;
  email: string;
  displayName: string;
  role: WorkspaceRole;
  invitedAt: string;
  joinedAt?: string;
}

export interface WorkspaceMembersResponse {
  members: WorkspaceMember[];
  total: number;
}

export interface InviteMemberRequest {
  /** Clerk sub（workspace_members.line_user_id にそのまま登録される識別子） */
  lineUserId: string;
  displayName?: string;
  role: "admin" | "member";
}

export interface UpdateMemberRoleRequest {
  role: WorkspaceRole;
}

// ─── Overall Stats API Types (Q-1) ───

export interface OverallStats {
  totalWorkspaces: number;
  totalUsers: number;
  totalDocuments: number;
  totalProposals: number;
  proposalApprovalRate: number;
  storageUsedBytes: number;
  llmTokensUsed: number;
  llmCostUsd: number;
  avgResponseMs: number;
  period: "day" | "week" | "month";
  periodStart: string;
  periodEnd: string;
}

export interface OverallStatsResponse {
  stats: OverallStats;
}

// ─── Benchmark Curation API Types ───

export type BenchmarkCurationAnnotationStatus = "todo" | "approved" | "rejected" | "adjudicated";
export type BenchmarkCurationPriorityBand = "temporal" | "business_key" | "claim_heavy" | "standard";
export type BenchmarkCurationQueueAction = "annotate" | "requeue" | "hold";

export interface BenchmarkCurationOverviewResponse {
  generatedAt: string | null;
  updatedAt: string | null;
  packetCount: number;
  caseCount: number;
  statusBreakdown: Record<BenchmarkCurationAnnotationStatus, number>;
}

export interface BenchmarkCurationPacketListItem {
  packetId: string;
  taskCaseId: string;
  scenarioId: string;
  sourceType: string;
  privacyTag: "internal" | "restricted";
  questionType: string;
  approvedQuestion: string;
  currentStatus: BenchmarkCurationAnnotationStatus;
  mandatoryClaimCount: number;
  requiredFactCount: number;
  mustHitEvidenceCount: number;
  queueAction: BenchmarkCurationQueueAction;
  priorityBand: BenchmarkCurationPriorityBand;
  priorityScore: number;
  reasons: string[];
}

export interface BenchmarkCurationPacketListResponse {
  items: BenchmarkCurationPacketListItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface BenchmarkCurationPacketDetail {
  packetId: string;
  taskCaseId: string;
  scenarioId: string;
  sourceType: string;
  privacyTag: "internal" | "restricted";
  redactedText: string;
  candidate: {
    candidateId: string;
    questionType: string;
    suggestedQuestion: string;
    evidenceSnippets: string[];
    mandatoryClaims: string[];
    optionalClaims: string[];
    abstainCondition: string;
    temporalHint?: string;
  };
  annotation: {
    status: BenchmarkCurationAnnotationStatus;
    approvedQuestion: string;
    referenceAnswer: string;
    mandatoryClaims: string[];
    optionalClaims: string[];
    forbiddenClaims: string[];
    mustHitEvidence: string[];
    requiredFacts: string[];
    abstainCondition: string;
    judgeCalibrationLabel?: "pass" | "fail" | "uncertain";
    notes: string;
  };
}

export interface BenchmarkCurationPacketDetailResponse {
  packet: BenchmarkCurationPacketDetail;
  queueMeta: {
    queueAction: BenchmarkCurationQueueAction;
    priorityBand: BenchmarkCurationPriorityBand;
    priorityScore: number;
    reasons: string[];
  } | null;
}

export interface BenchmarkCurationQueueEntry {
  rank: number;
  packetId: string;
  taskCaseId: string;
  sourceType: string;
  privacyTag: "internal" | "restricted";
  scenarioId: string;
  questionType: string;
  approvedQuestion: string;
  currentStatus: BenchmarkCurationAnnotationStatus;
  queueAction: BenchmarkCurationQueueAction;
  priorityBand: BenchmarkCurationPriorityBand;
  priorityScore: number;
  reasons: string[];
}

export interface BenchmarkCurationQueueResponse {
  queue: BenchmarkCurationQueueEntry[];
  report: {
    totalPackets: number;
    eligiblePackets: number;
    includedPackets: number;
    outputLimit: number | null;
    statusBreakdown: Record<BenchmarkCurationAnnotationStatus, number>;
    queueActionBreakdown: Record<BenchmarkCurationQueueAction, number>;
    priorityBandBreakdown: Record<BenchmarkCurationPriorityBand, number>;
    temporalPackets: number;
    businessKeyPackets: number;
    claimHeavyPackets: number;
    requeuePackets: number;
    holdPackets: number;
    topPacketId: string | null;
    topScore: number | null;
  };
}

export interface BenchmarkCurationPacketUpdateRequest {
  status?: BenchmarkCurationAnnotationStatus;
  approvedQuestion?: string;
  referenceAnswer?: string;
  mandatoryClaims?: string[];
  optionalClaims?: string[];
  forbiddenClaims?: string[];
  mustHitEvidence?: string[];
  requiredFacts?: string[];
  abstainCondition?: string;
  judgeCalibrationLabel?: "pass" | "fail" | "uncertain" | null;
  notes?: string;
}

// ─── J-LoCoMo Curation API Types ───

export type JLocomoBenchmarkSlice =
  | "single-session"
  | "cross-session"
  | "temporal"
  | "multihop"
  | "persona-consistency";

export type JLocomoCurationReviewStatus = "draft" | "reviewed" | "adjudicated";
export type JLocomoCurationPriorityBand = "temporal" | "cross-session" | "persona" | "standard";
export type JLocomoCurationQueueAction = "review" | "adjudicate" | "spot-check";

export interface TemporalSpec {
  referenceTime: string;
  targetWindow: {
    start: string;
    end: string;
  };
  expectedRelation:
    | "within_window"
    | "latest_before_reference"
    | "aggregate_over_window"
    | "deadline_by_window_end";
  allowAbstain?: boolean;
  timeZone?: string;
}

export interface JLocomoCurationOverviewResponse {
  datasetId: string;
  generatedAt: string | null;
  updatedAt: string | null;
  conversationCount: number;
  packetCount: number;
  statusBreakdown: Record<JLocomoCurationReviewStatus, number>;
  splitBreakdown: Record<"train" | "eval", number>;
  sliceBreakdown: Record<JLocomoBenchmarkSlice, number>;
}

export interface JLocomoCurationPacketListItem {
  packetId: string;
  conversationId: string;
  conversationTitle: string;
  qaId: string;
  split: "train" | "eval";
  reviewStatus: JLocomoCurationReviewStatus;
  benchmarkSlices: JLocomoBenchmarkSlice[];
  query: string;
  evidenceCount: number;
  requiredFactCount: number;
  hasTemporalSpec: boolean;
  priorityBand: JLocomoCurationPriorityBand;
  priorityScore: number;
  queueAction: JLocomoCurationQueueAction;
  reasons: string[];
}

export interface JLocomoCurationPacketListResponse {
  items: JLocomoCurationPacketListItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface JLocomoCurationPacketDetail {
  packetId: string;
  datasetId: string;
  conversationId: string;
  conversationTitle: string;
  qaId: string;
  split: "train" | "eval";
  reviewStatus: JLocomoCurationReviewStatus;
  benchmarkSlices: JLocomoBenchmarkSlice[];
  query: string;
  expectedAnswer: string;
  evidenceDocumentIds: string[];
  mustHitEvidence: string[];
  requiredFacts: string[];
  temporalSpec?: TemporalSpec;
  notes: string;
  updatedAt?: string;
}

export interface JLocomoCurationConversationContext {
  conversationId: string;
  title: string;
  split: "train" | "eval";
  participants: Array<{ id: string; name: string; role?: string }>;
  summary: {
    sessionCount: number;
    turnCount: number;
    qaCount: number;
  };
  outline: Array<{
    sessionId: string;
    title: string;
    occurredAt: string;
    turnCount: number;
    isEvidenceSession: boolean;
    isFocusSession: boolean;
  }>;
  focusSessions: Array<{
    sessionId: string;
    title: string;
    occurredAt: string;
    isEvidenceSession: boolean;
    turns: Array<{
      documentId: string;
      turnId: string;
      speakerId: string;
      speakerName: string;
      text: string;
      occurredAt: string;
      isEvidence: boolean;
      isMustHit: boolean;
    }>;
  }>;
}

export interface JLocomoCurationPacketDetailResponse {
  packet: JLocomoCurationPacketDetail;
  queueMeta: {
    queueAction: JLocomoCurationQueueAction;
    priorityBand: JLocomoCurationPriorityBand;
    priorityScore: number;
    reasons: string[];
  } | null;
  conversationContext: JLocomoCurationConversationContext | null;
  sourcePacket: JLocomoCurationPacketDetail | null;
  fieldDiffs: Array<{
    field:
      | 'query'
      | 'expectedAnswer'
      | 'benchmarkSlices'
      | 'evidenceDocumentIds'
      | 'mustHitEvidence'
      | 'requiredFacts'
      | 'temporalSpec'
      | 'notes'
      | 'reviewStatus'
    sourceValue: string
    currentValue: string
  }>;
}

export interface JLocomoCurationQueueEntry {
  rank: number;
  packetId: string;
  conversationId: string;
  conversationTitle: string;
  qaId: string;
  split: "train" | "eval";
  reviewStatus: JLocomoCurationReviewStatus;
  queueAction: JLocomoCurationQueueAction;
  priorityBand: JLocomoCurationPriorityBand;
  priorityScore: number;
  benchmarkSlices: JLocomoBenchmarkSlice[];
  query: string;
  reasons: string[];
}

export interface JLocomoCurationQueueResponse {
  queue: JLocomoCurationQueueEntry[];
  report: {
    totalPackets: number;
    includedPackets: number;
    outputLimit: number | null;
    statusBreakdown: Record<JLocomoCurationReviewStatus, number>;
    priorityBandBreakdown: Record<JLocomoCurationPriorityBand, number>;
    queueActionBreakdown: Record<JLocomoCurationQueueAction, number>;
    topPacketId: string | null;
    topScore: number | null;
  };
}

export interface JLocomoArtifactsStatusResponse {
  sourceExists: boolean
  packetsExists: boolean
  compiledExists: boolean
  datasetId: string | null
  packetCount: number
  report: {
    generatedAt?: string
    usedPackets?: boolean
    report?: {
      datasetId: string
      conversationCount: number
      sessionCount: number
      turnCount: number
      qaCount: number
      splitCounts: Record<'train' | 'eval', number>
      reviewStatusCounts: Record<'draft' | 'reviewed' | 'adjudicated', number>
      sliceCounts: Record<JLocomoBenchmarkSlice, number>
      validationIssues: string[]
    }
  } | null
}

export interface JLocomoReadinessResponse {
  datasetId: string | null
  packetCount: number
  reviewProgress: {
    overall: Record<'draft' | 'reviewed' | 'adjudicated', number>
    eval: Record<'draft' | 'reviewed' | 'adjudicated', number>
    evalTotal: number
    evalAdjudicatedRate: number
  }
  judgeConfiguration: {
    generationModel: { provider: string; model: string } | null
    judgeModel: { provider: string; model: string } | null
    judgeConfigured: boolean
    judgeSeparated: boolean
    missing: string[]
  }
  latestSmokeRuns: {
    recall: {
      suite: 'recall'
      timestamp: string
      path: string
      generationModel: { provider: string; model: string } | null
      judgeModel: { provider: string; model: string } | null
      judgeSeparated: boolean
    } | null
    answerQuality: {
      suite: 'answer-quality'
      timestamp: string
      path: string
      generationModel: { provider: string; model: string } | null
      judgeModel: { provider: string; model: string } | null
      judgeSeparated: boolean
    } | null
  }
  blockers: string[]
}

export interface JLocomoCurationValidateResponse {
  datasetId: string
  conversationCount: number
  sessionCount: number
  turnCount: number
  qaCount: number
  compileIssues: string[]
  effectiveIssues: string[]
  strictPilot: boolean
  usedPackets: boolean
}

export interface JLocomoCurationCompileResponse {
  datasetId: string
  outputDir: string
  generatedAt: string
  usedPackets: boolean
  report: {
    datasetId: string
    conversationCount: number
    sessionCount: number
    turnCount: number
    qaCount: number
    splitCounts: Record<'train' | 'eval', number>
    reviewStatusCounts: Record<'draft' | 'reviewed' | 'adjudicated', number>
    sliceCounts: Record<JLocomoBenchmarkSlice, number>
    validationIssues: string[]
  }
}

export interface JLocomoCurationSmokeRunResponse {
  suite: 'recall' | 'answer-quality'
  dryRun: boolean
  queryCount: number
  extractionCount: number
  goldenPath: string
  outputDir: string
  result: {
    suite: string
    timestamp: string
    metrics: Record<string, number>
    details: unknown
    provenance?: unknown
  }
}

export interface JLocomoCurationPacketUpdateRequest {
  reviewStatus?: JLocomoCurationReviewStatus;
  benchmarkSlices?: JLocomoBenchmarkSlice[];
  query?: string;
  expectedAnswer?: string;
  evidenceDocumentIds?: string[];
  mustHitEvidence?: string[];
  requiredFacts?: string[];
  temporalSpec?: TemporalSpec | null;
  notes?: string;
}

export interface JLocomoCurationValidateRequest {
  strictPilot?: boolean
}

export interface JLocomoCurationSmokeRunRequest {
  suite: 'recall' | 'answer-quality'
  dryRun?: boolean
  queryCount?: number
}

// ─── Search API Types (LQ-10) ───

export type SearchStrategy =
  | "hybrid"
  | "hybrid-lite"
  | "bm25"
  | "graph"
  | "default"
  | "vector"
  | "manual-only";

export interface SearchResultItem {
  text: string;
  score: number;
  sourceType: string;
  documentId: string;
  createdAt: string;
}

export interface SearchResponse {
  results: SearchResultItem[];
  query: string;
  diagnostics?: {
    degraded: boolean;
    failures: Array<{
      stage: string;
      fallback: string;
      reason: string;
      details?: string;
    }>;
  };
}

export interface SearchRequest {
  query: string;
  workspaceId: string;
  strategy?: SearchStrategy;
  limit?: number;
  sourceType?: "file";
}

// ─── Catalog API Types (CL-05) ───
// GET /api/web-ui/catalog/kpi の DTO（集計値のみ・read-only）。
// core の CatalogSummary を JSON 越しに受ける形（Date は ISO string で来る）。

/** 🟢 稼働 / 🟡 準備済 / 🔴 未対応 */
export type TierItemStatus = "active" | "ready" | "missing";

export interface TierItem {
  label: string;
  status: TierItemStatus;
  note?: string;
}

export interface CatalogObjectKpi {
  sobjectApiName: string;
  /** ISO 文字列（JSON シリアライズ後）。未掌握なら null */
  snapshotAt: string | null;
  /** now - snapshotAt の経過 ms。covered=false なら null */
  ageMs: number | null;
  covered: boolean;
}

export interface CatalogKpi {
  /** 期待 object のうち snapshot を持つ割合（0-1） */
  objectCoverage: number;
  coveredCount: number;
  expectedCount: number;
  /** snapshot を持たない object */
  missingObjects: string[];
  /** 最も古い掌握の経過 ms（= 最悪の変化検知遅延）。covered 0 なら null */
  deltaAwarenessLatencyMs: number | null;
  perObject: CatalogObjectKpi[];
}

export interface CatalogSummaryResponse {
  tierA: CatalogKpi;
  tierB: TierItem[];
  tierC: TierItem[];
  /** 全体掌握率（Tier A の object_coverage を主指標に） */
  overallCoverage: number;
}

// ─── Access Management API Types (UAC-08b) ───
// GET/POST/DELETE /api/admin/workspaces/:id/access-policies, /api/admin/workspaces/:id/mcp-tokens の DTO。
// core の HokageAccessPolicyAdminRecord / ApiKey（access-policy-admin.ts / api-key-service.ts）を
// JSON 越しに受ける形（Date は ISO string で来る）。

/**
 * individual tool/source policy が対象にできる resource_engine（UAC-07 admin scope 限定）。
 * UAC-FIX-01: 正準値は core の access-policy-resources.ts（spec §12.4）と同期する
 * （層1 = "mcp_tool"、層2 = "source"。旧値 "tool" はサーバー側で明示エラー）。
 */
export type UacAdminResourceEngine = "mcp_tool" | "source";

export const HOKAGE_DATA_CLASSES = [
  "tool_connection",
  "aggregate_summary",
  "customer_status",
  "customer_pii_raw",
  "mcp_tool",
] as const;
export type HokageDataClass = typeof HOKAGE_DATA_CLASSES[number];

export const HOKAGE_ACTIONS = ["view", "edit", "delete"] as const;
export type HokageAction = typeof HOKAGE_ACTIONS[number];

export interface AccessPolicy {
  id: string;
  workspaceId: string;
  resourceEngine: UacAdminResourceEngine;
  resourceKind: string;
  resourceId: string;
  accessScope: "individual";
  principalRef: string;
  dataClass: HokageDataClass;
  actions: HokageAction[];
  effect: "allow" | "deny";
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface AccessPoliciesListResponse {
  policies: AccessPolicy[];
  total: number;
}

export interface CreateAccessPolicyRequest {
  resourceEngine: UacAdminResourceEngine;
  resourceKind: string;
  resourceId: string;
  principalRef: string;
  dataClass?: HokageDataClass;
  actions?: HokageAction[];
  effect?: "allow" | "deny";
  reason?: string;
}

/** MCP 鍵専用スコープ。"mcp" のみを持つ鍵は既存 core REST API にはアクセスできない（UAC-07）。 */
export type McpTokenScope = "read" | "write" | "admin" | "mcp";

export interface McpToken {
  id: string;
  workspaceId: string;
  name: string;
  keyPrefix: string;
  scopes: McpTokenScope[];
  /**
   * UCX-01: 鍵に紐づく userSlot（Hokage user-directory の dataUserSlot）。
   * null = 制限なし（後方互換）。既存レスポンスとの互換のため optional。
   */
  userSlot?: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface McpTokensListResponse {
  mcpTokens: McpToken[];
  total: number;
}

export interface CreateMcpTokenRequest {
  name: string;
  expiresAt?: string;
  /** UCX-01/UCX-04: 発行時に紐づける userSlot（省略 = 制限なし・後方互換）。 */
  userSlot?: string;
}

export interface CreateMcpTokenResponse {
  mcpToken: McpToken;
  /** 平文キー — 発行レスポンスでのみ返却される。以後は再取得不可。 */
  rawKey: string;
}

// ─── userSlot 選択肢 API Types (UCX-04) ───
// GET /api/admin/workspaces/:id/user-slots の DTO。
// core の HokageUserSlotOption（admin.ts）を JSON 越しに受ける形。

/** MCP 鍵発行ウィザードの Step1（対象者選択）が使う userSlot 選択肢。 */
export interface HokageUserSlotOption {
  userSlot: string;
  displayName: string;
  departments: string[];
  roles: string[];
}

export interface UserSlotsListResponse {
  userSlots: HokageUserSlotOption[];
  total: number;
}

export type SourceReadProfile = 'all_sources_all_fieldy' | 'all_sources_own_fieldy' | 'configured_without_fieldy';
export interface SourceReadProfileState {
  profile: SourceReadProfile | null;
  effectiveFieldyScope: 'all' | 'own' | 'none';
  conflicts: string[];
}
